import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Gauge, Loader2 } from 'lucide-react';

import aiPromptService from '../../services/aiPromptService';
import type { AnalysisLimits } from '../../types/projectAnalysis';
import { AnalysisLimitsTable } from './AnalysisLimitsTable';

/** Read-only effective limits of an organization (org admins; set by the platform). */
export const EffectiveAnalysisLimitsCard: React.FC<{ organizationId?: string | null }> = ({ organizationId }) => {
  const { t } = useTranslation();
  const [limits, setLimits] = useState<AnalysisLimits | null>(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLimits(null);
    setLoadError(false);
    aiPromptService
      .limits(organizationId)
      .then((data) => {
        if (!cancelled) setLimits(data);
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  return (
    <section className="space-y-3 rounded-lg border border-border bg-card p-4">
      <div>
        <h3 className="flex items-center gap-2 text-base font-semibold">
          <Gauge className="h-4 w-4" />
          {t('projectAnalysisLimits.effectiveTitle')}
        </h3>
        <p className="text-xs text-muted-foreground">{t('projectAnalysisLimits.effectiveDescription')}</p>
      </div>
      {loadError ? (
        <p className="text-sm text-destructive">{t('projectAnalysisLimits.loadError')}</p>
      ) : !limits ? (
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
      ) : (
        <AnalysisLimitsTable
          limits={limits}
          inheritedOf={(key) => limits.defaults[key]}
          inheritedLabel={t('projectAnalysisLimits.columns.default')}
        />
      )}
    </section>
  );
};

export default EffectiveAnalysisLimitsCard;
